import { useEffect, useRef, useState, type ReactNode } from 'react';

import { CurrencyContext, type Currency } from '../../contexts/CurrencyContext';

const STORAGE_KEY = 'passwd:currency';

const getDefaultCurrency = (locale: string): Currency => (locale === 'en' ? 'usd' : 'eur');

const getStoredCurrency = (): Currency | null => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);

        if (stored === 'eur' || stored === 'usd') {
            return stored;
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to read currency from localStorage:', error);
    }

    return null;
};

interface CurrencyProviderProps {
    locale: string;
    children: ReactNode;
}

export const CurrencyProvider = ({ locale, children }: CurrencyProviderProps) => {
    // The server render and the client's first (hydration) render must be identical, so we always
    // start from the locale default. The persisted choice is adopted after hydration, in the effect
    // below — reading localStorage during render would break hydration for returning visitors.
    const [currency, setCurrency] = useState<Currency>(() => getDefaultCurrency(locale));

    useEffect(() => {
        const stored = getStoredCurrency();

        if (stored) {
            setCurrency(stored);
        }
    }, []);

    // Skip the initial render so the default value doesn't overwrite the persisted one before the
    // effect above has adopted it.
    const isInitialRender = useRef(true);

    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;

            return;
        }

        try {
            localStorage.setItem(STORAGE_KEY, currency);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to save currency to localStorage:', error);
        }
    }, [currency]);

    return <CurrencyContext.Provider value={{ currency, setCurrency }}>{children}</CurrencyContext.Provider>;
};
